import { useCallback, useEffect, useMemo, useState } from 'react'
import toast from 'react-hot-toast'
import { getProjectFlows, setProjectFlowActive, setProjectFlowBudget } from '@/api/integration'
import type { FlowBudget, FlowUsage, ProjectFlow, ProjectFlowRun, ProjectFlowsResponse } from '@/api/integration'
import FlowSourcePanel from './FlowSourcePanel'

/**
 * Flows section of Events & APIs — every IOFlow flow tied to the project,
 * whichever direction it runs: flows triggered by this app's events, flows
 * that call its APIs, and the project's own flows (built for it, driven by a
 * Scheduler, opened from a menu item).
 *
 * Everything is read live from IOFlow through the screen's existing IOFlow
 * connection (`/integration/flows`); nothing is stored in Twasta. Activate /
 * Pause act on the flow in IOFlow (`/integration/flow-state`).
 *
 * Usage & cost come from IOFlow too: it meters every paid API answer a flow
 * gets (posts read, tokens used) per run and step, priced from the connector's
 * list prices or the connection's own. The flow owner sees what each flow and
 * run cost and may set a daily budget, which IOFlow enforces
 * (`/integration/flow-budget`).
 */

type FilterKey = 'all' | 'events' | 'apis' | 'scheduled' | 'failing'

const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'events', label: 'Use this app’s events' },
  { key: 'apis', label: 'Call this app’s APIs' },
  { key: 'scheduled', label: 'Scheduled' },
  { key: 'failing', label: 'Failing' },
]

const DIRECTION_LABEL: Record<ProjectFlow['direction'], string> = {
  events: 'Listens to this app',
  apis: 'Calls this app',
  both: 'Listens + calls',
  owned: 'This app’s flow',
}

const USE_LABEL: Record<string, string> = {
  event: 'Event',
  api: 'API',
  scheduler: 'Scheduler',
  menu: 'Menu item',
  built: 'Built for this app',
}

const TRIGGER_LABEL: Record<string, string> = {
  event: 'On event',
  application_event: 'On event',
  schedule: 'Schedule',
  manual: 'Manual',
  webhook: 'Webhook',
  polling: 'Polling',
}

const RUN_LABEL: Record<string, string> = {
  completed: 'Succeeded',
  failed: 'Failed',
  running: 'Running',
  pending: 'Pending',
  queued: 'Queued',
  cancelled: 'Cancelled',
}

function isToday(iso?: string | null): boolean {
  if (!iso) return false
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return false
  const now = new Date()
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth() && d.getDate() === now.getDate()
}

function whenText(iso?: string | null): string {
  if (!iso) return '—'
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return String(iso)
  const time = d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  if (isToday(iso)) return `Today ${time}`
  const y = new Date()
  y.setDate(y.getDate() - 1)
  if (d.toDateString() === y.toDateString()) return `Yesterday ${time}`
  return `${d.toLocaleDateString([], { day: 'numeric', month: 'short' })} ${time}`
}

function durationText(s?: number | null): string {
  if (s == null || s < 0) return '—'
  if (s < 1) return `${Math.round(s * 1000)} ms`
  if (s < 60) return `${s.toFixed(1)} s`
  const m = Math.floor(s / 60)
  return `${m} min ${Math.round(s - m * 60)} s`
}

function money(v: number | null | undefined, currency = 'USD'): string {
  if (v == null || Number.isNaN(v)) return '—'
  // Sub-cent amounts (a cheap model's call) keep enough digits to be seen.
  const digits = v !== 0 && Math.abs(v) < 0.01 ? 4 : 2
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency', currency, minimumFractionDigits: digits, maximumFractionDigits: digits,
    }).format(v)
  } catch {
    return `${currency} ${v.toFixed(digits)}`
  }
}

function quantityText(n: number): string {
  return n >= 1_000_000 ? `${(n / 1_000_000).toFixed(2)} M` : Math.round(n).toLocaleString()
}

function errorMessage(e: unknown, fallback: string): string {
  const detail = (e as { response?: { data?: { detail?: string } } })?.response?.data?.detail
  return typeof detail === 'string' ? detail : fallback
}

interface FlowStats {
  failedToday: number
  runsToday: number
  lastError: ProjectFlowRun | null
}

function statsOf(flow: ProjectFlow): FlowStats {
  const runs = flow.runs || []
  const today = runs.filter((r) => isToday(r.started_at))
  const lastError = today.find((r) => r.status === 'failed') || null
  return {
    runsToday: today.length,
    failedToday: today.filter((r) => r.status === 'failed').length,
    lastError,
  }
}

interface Props {
  project: string
  /** Activate / Pause need the project's edit right. */
  canEdit: boolean
  /** The app has been synced to IOFlow (else the section explains how to start). */
  synced: boolean
  /** Bumps when the design is synced again — re-read the flows. */
  refreshKey?: string
  onOpen: (target: 'flow' | 'flows' | 'executions', flowId?: string) => void
  onSync?: () => void
  /** The flows once read, so the Events / APIs rows can say who uses them. */
  onFlows?: (flows: ProjectFlow[]) => void
}

export default function IntegrationFlowsSection({
  project, canEdit, synced, refreshKey, onOpen, onSync, onFlows,
}: Props) {
  const [data, setData] = useState<ProjectFlowsResponse | null>(null)
  const [loading, setLoading] = useState(true)
  const [readAt, setReadAt] = useState<Date | null>(null)
  const [filter, setFilter] = useState<FilterKey>('all')
  const [open, setOpen] = useState<Record<string, boolean>>({})
  const [busy, setBusy] = useState<string>('')

  const load = useCallback(async () => {
    if (!project) return
    setLoading(true)
    try {
      const res = await getProjectFlows(project)
      setData(res)
      setReadAt(new Date())
      onFlows?.(res.flows || [])
    } catch (e) {
      console.error('Failed to read flows from IOFlow', e)
      setData({ success: false, reachable: false, synced, flows: [], error: errorMessage(e, 'Could not read flows from IOFlow') })
    } finally {
      setLoading(false)
    }
    // onFlows is a notification only — a new identity must not re-read IOFlow.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project, synced])

  useEffect(() => {
    load()
  }, [load, refreshKey])

  const flows = useMemo(() => data?.flows || [], [data])
  const stats = useMemo(() => {
    const m: Record<string, FlowStats> = {}
    for (const f of flows) m[f.flow_id] = statsOf(f)
    return m
  }, [flows])

  const matches = useCallback(
    (f: ProjectFlow, key: FilterKey) => {
      switch (key) {
        case 'events': return f.direction === 'events' || f.direction === 'both'
        case 'apis': return f.direction === 'apis' || f.direction === 'both'
        case 'scheduled': return f.scheduled
        case 'failing': return (stats[f.flow_id]?.failedToday || 0) > 0
        default: return true
      }
    },
    [stats],
  )

  const toggle = async (flow: ProjectFlow) => {
    const next = !flow.active
    setBusy(flow.flow_id)
    try {
      const updated = await setProjectFlowActive(project, flow.flow_id, next)
      setData((d) => {
        if (!d) return d
        const flowsNext = d.flows.map((f) => (f.flow_id === flow.flow_id ? { ...f, ...updated, runs: f.runs, runs_error: f.runs_error } : f))
        onFlows?.(flowsNext)
        return { ...d, flows: flowsNext }
      })
      toast.success(
        next
          ? `Activated in IOFlow: ${flow.name}`
          : `Paused in IOFlow: ${flow.name}${flow.scheduled ? ' — its schedule won’t run until you activate it' : ''}`,
      )
    } catch (e) {
      toast.error(errorMessage(e, next ? 'Could not activate the flow' : 'Could not pause the flow'))
    } finally {
      setBusy('')
    }
  }

  const saveBudget = async (flow: ProjectFlow, daily: number | null) => {
    setBusy(flow.flow_id)
    try {
      const budget = await setProjectFlowBudget(project, flow.flow_id, daily)
      setData((d) => {
        if (!d) return d
        const flowsNext = d.flows.map((f) => {
          if (f.flow_id !== flow.flow_id || !f.usage) return f
          const spent = f.usage.today_cost
          const next: FlowBudget | null = budget ? { ...budget, spent_today: spent, reached: spent >= budget.daily } : null
          return { ...f, usage: { ...f.usage, budget: next } }
        })
        return { ...d, flows: flowsNext }
      })
      toast.success(daily ? `Daily budget set for ${flow.name}` : `Daily budget removed for ${flow.name}`)
    } catch (e) {
      toast.error(errorMessage(e, 'Could not save the budget'))
    } finally {
      setBusy('')
    }
  }

  const total = flows.length
  const activeCount = flows.filter((f) => f.active).length
  const scheduledCount = flows.filter((f) => f.scheduled).length
  const failingFlows = flows.filter((f) => (stats[f.flow_id]?.failedToday || 0) > 0)
  const failedRuns = failingFlows.reduce((n, f) => n + (stats[f.flow_id]?.failedToday || 0), 0)
  const shown = flows.filter((f) => matches(f, filter))
  const metered = flows.filter((f) => f.usage)
  const costCurrency = metered[0]?.usage?.currency || 'USD'
  const costToday = metered.reduce((n, f) => n + (f.usage?.today_cost || 0), 0)
  const cost7 = metered.reduce((n, f) => n + (f.usage?.last_7_days_cost || 0), 0)

  return (
    <div className="card" style={{ marginBottom: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', marginBottom: '0.25rem', flexWrap: 'wrap' }}>
        <h3 style={{ margin: 0, fontSize: '1.1125rem', flex: 1, minWidth: 220 }}>
          <span className="material-icons" style={{ fontSize: 21, verticalAlign: 'text-bottom', marginRight: 6, color: 'var(--color-accent)' }}>account_tree</span>
          Flows
          <span style={{ fontWeight: 400, fontSize: '0.8625rem', marginLeft: 8, color: 'var(--color-text-secondary)' }}>
            in IOFlow · both directions
          </span>
        </h3>
        {readAt && (
          <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', alignSelf: 'center' }}>
            Read from IOFlow at {readAt.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
          </span>
        )}
        <button className="btn btn-secondary" onClick={load} disabled={loading}>
          {loading ? (
            <span className="spinner" style={{ width: 16, height: 16, borderWidth: 2, marginRight: 4 }} />
          ) : (
            <span className="material-icons" style={{ fontSize: 17, marginRight: 4 }}>refresh</span>
          )}
          Refresh
        </button>
      </div>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
        Every IOFlow flow that listens to this app’s events or calls its APIs — including scheduled
        ones and the flows its Schedulers and menu items run. Status and runs are read from IOFlow
        each time; each flow’s design is also kept in the app’s source, so it travels with git.
      </p>
      <FlowSourcePanel project={project} canEdit={canEdit} onRestored={load} />

      {loading && !data ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '1.5rem' }}>
          <div className="spinner" />
        </div>
      ) : data && !data.reachable ? (
        <div
          role="alert"
          style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
            padding: '0.6rem 0.75rem', borderRadius: 8,
            background: 'var(--color-warning-weak)', color: 'var(--color-warning)',
          }}
        >
          <span className="material-icons" style={{ fontSize: 19 }}>warning</span>
          <span style={{ flex: 1, minWidth: 220 }}>
            Couldn’t read flows from IOFlow{data.error ? ` (${data.error})` : ''}. Your events and APIs are unaffected.
          </span>
          <button className="btn btn-secondary" onClick={load}>Try again</button>
        </div>
      ) : total === 0 ? (
        <div
          style={{
            display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
            padding: '0.85rem', borderRadius: 8, border: '1px dashed var(--color-border-strong, var(--color-border))',
          }}
        >
          <div style={{ flex: 1, minWidth: 240 }}>
            {synced ? (
              <>
                <div style={{ fontWeight: 600 }}>No flows use this app’s events or APIs</div>
                <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                  E.g. a flow that emails the customer when an order is created, or one that imports
                  orders every 10 minutes.
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight: 600 }}>This app isn’t in IOFlow yet</div>
                <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
                  Sync your events and APIs first — then every flow that uses them shows here, with
                  its schedule and recent runs.
                </div>
              </>
            )}
          </div>
          {!synced && canEdit && onSync && (
            <button className="btn btn-primary" onClick={onSync}>Sync to IOFlow</button>
          )}
          {synced && (
            <button className="btn btn-secondary" onClick={() => onOpen('flows')}>
              <span className="material-icons" style={{ fontSize: 17, marginRight: 4 }}>open_in_new</span>
              Open in IOFlow
            </button>
          )}
        </div>
      ) : (
        <>
          {/* Summary */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.6rem', marginBottom: '0.75rem' }}>
            <SummaryBox label="Flows tied to this app" value={String(total)} />
            <SummaryBox label="Active" value={String(activeCount)} sub={`· ${total - activeCount} paused`} />
            <SummaryBox label="Scheduled" value={String(scheduledCount)} />
            <SummaryBox
              label="Failing today"
              value={String(failingFlows.length)}
              sub={failedRuns ? `· ${failedRuns} failed run${failedRuns === 1 ? '' : 's'}` : ''}
              danger={failingFlows.length > 0}
            />
            {metered.length > 0 && (
              <SummaryBox
                label="Paid API calls today"
                value={`≈ ${money(costToday, costCurrency)}`}
                sub={`· ${money(cost7, costCurrency)} last 7 days`}
              />
            )}
          </div>

          {/* Filter */}
          <div role="group" aria-label="Show flows" style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: '0.75rem' }}>
            {FILTERS.map((f) => {
              const on = filter === f.key
              const count = flows.filter((x) => matches(x, f.key)).length
              return (
                <button
                  key={f.key}
                  type="button"
                  aria-pressed={on}
                  onClick={() => setFilter(f.key)}
                  className={on ? 'btn btn-primary' : 'btn btn-secondary'}
                  style={{ borderRadius: 999, padding: '4px 12px', fontSize: '0.8125rem' }}
                >
                  {f.label} <span style={{ opacity: 0.75, marginLeft: 4 }}>{count}</span>
                </button>
              )
            })}
          </div>

          {shown.length === 0 ? (
            <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>No flows match this filter.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              {shown.map((flow) => (
                <FlowRow
                  key={flow.flow_id}
                  flow={flow}
                  stats={stats[flow.flow_id]}
                  open={!!open[flow.flow_id]}
                  busy={busy === flow.flow_id}
                  canEdit={canEdit}
                  onToggleOpen={() => setOpen((o) => ({ ...o, [flow.flow_id]: !o[flow.flow_id] }))}
                  onToggleActive={() => toggle(flow)}
                  onSaveBudget={(daily) => saveBudget(flow, daily)}
                  onOpen={onOpen}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  )
}

function SummaryBox({ label, value, sub, danger }: { label: string; value: string; sub?: string; danger?: boolean }) {
  return (
    <div
      style={{
        border: `1px solid ${danger ? 'var(--color-error)' : 'var(--color-border)'}`,
        background: danger ? 'var(--color-error-weak)' : undefined,
        borderRadius: 8, padding: '0.5rem 0.75rem',
      }}
    >
      <div style={{ fontSize: '0.8125rem', color: danger ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>{label}</div>
      <div style={{ fontSize: '1.25rem', fontWeight: 600, color: danger ? 'var(--color-error)' : undefined }}>
        {value}
        {sub && <span style={{ fontSize: '0.8125rem', fontWeight: 400, marginLeft: 6 }}>{sub}</span>}
      </div>
    </div>
  )
}

interface FlowRowProps {
  flow: ProjectFlow
  stats: FlowStats
  open: boolean
  busy: boolean
  canEdit: boolean
  onToggleOpen: () => void
  onToggleActive: () => void
  onSaveBudget: (daily: number | null) => void
  onOpen: (target: 'flow' | 'flows' | 'executions', flowId?: string) => void
}

function FlowRow({ flow, stats, open, busy, canEdit, onToggleOpen, onToggleActive, onSaveBudget, onOpen }: FlowRowProps) {
  const runs = flow.runs || []
  const failing = stats.failedToday > 0
  const trigger = TRIGGER_LABEL[flow.trigger_type] || flow.trigger_type || 'Manual'
  const eventUse = flow.uses.find((u) => u.kind === 'event')
  const triggerText = flow.scheduled
    ? flow.schedule_text || 'on a schedule'
    : eventUse
      ? eventUse.name
      : flow.trigger_type === 'manual'
        ? 'started from IOFlow or a menu item'
        : ''
  const nextText = !flow.active
    ? 'not running while paused'
    : flow.scheduled && flow.next_run
      ? `next run ${whenText(flow.next_run)}`
      : ''
  const runsSummary = flow.runs_error
    ? 'Runs could not be read'
    : runs.length === 0
      ? 'No runs yet'
      : stats.runsToday > 0
        ? `${stats.runsToday} run${stats.runsToday === 1 ? '' : 's'} today${stats.failedToday ? ` · ${stats.failedToday} failed` : ' · all succeeded'}`
        : `Last run ${whenText(runs[0].started_at)}`
  // Oldest → newest, left to right.
  const bars = [...runs].reverse()
  const usage = flow.usage
  const currency = usage?.currency || 'USD'
  const budget = usage?.budget

  return (
    <div
      style={{
        borderRadius: 8,
        border: `1px solid ${failing && flow.active ? 'var(--color-error)' : 'var(--color-border)'}`,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem', padding: '0.6rem 0.75rem', flexWrap: 'wrap' }}>
        <span
          className="material-icons"
          style={{ fontSize: 19, marginTop: 2, color: flow.active ? 'var(--color-accent)' : 'var(--color-text-secondary)' }}
        >
          account_tree
        </span>
        <div style={{ flex: 1, minWidth: 260, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span style={{ fontWeight: 600 }}>{flow.name}</span>
            <span className={flow.active ? 'badge badge-success' : 'badge badge-warning'}>
              {flow.active ? 'Active' : flow.status === 'draft' ? 'Draft' : 'Paused'}
            </span>
            <span className="badge">{DIRECTION_LABEL[flow.direction]}</span>
          </div>
          <div style={{ fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
            <strong style={{ color: 'var(--color-text)', fontWeight: 600 }}>{trigger}</strong>
            {triggerText ? ` · ${triggerText}` : ''}
            {nextText ? <span style={{ marginLeft: 6 }}>· {nextText}</span> : null}
          </div>
          {flow.uses.length > 0 && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', fontSize: '0.8125rem' }}>
              <span style={{ color: 'var(--color-text-secondary)' }}>Uses</span>
              {flow.uses.map((u) => (
                <span
                  key={`${u.kind}:${u.name}`}
                  style={{
                    padding: '1px 8px', borderRadius: 6,
                    background: u.kind === 'event' ? 'var(--color-accent-weak)' : 'var(--color-surface-2, var(--color-bg))',
                    color: u.kind === 'event' ? 'var(--color-accent)' : 'var(--color-text)',
                  }}
                >
                  {USE_LABEL[u.kind] || u.kind}{' '}
                  {u.kind === 'event' || u.kind === 'api' ? (
                    <span style={{ fontFamily: 'monospace' }}>{u.name}</span>
                  ) : (
                    u.kind !== 'built' && u.name
                  )}
                </span>
              ))}
            </div>
          )}
          {stats.lastError && (
            <div
              style={{
                display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: '0.8625rem',
                color: 'var(--color-error)', background: 'var(--color-error-weak)', borderRadius: 6, padding: '4px 8px',
              }}
            >
              <span className="material-icons" style={{ fontSize: 16, marginTop: 1 }}>error_outline</span>
              <span>
                <strong>{whenText(stats.lastError.started_at)}</strong> · {stats.lastError.error || 'Run failed'}
                {stats.failedToday > 1 ? ` (${stats.failedToday} runs failed today)` : ''}
              </span>
            </div>
          )}
          {flow.runs_error && (
            <div style={{ fontSize: '0.8125rem', color: 'var(--color-warning)' }}>
              IOFlow didn’t return this flow’s runs: {flow.runs_error}
            </div>
          )}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 6 }}>
          {bars.length > 0 && (
            <div
              role="img"
              aria-label={`Last ${runs.length} runs: ${runs.filter((r) => r.status === 'failed').length} failed`}
              style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 22 }}
            >
              {bars.map((r, i) => (
                <span
                  key={r.execution_id || i}
                  title={`${whenText(r.started_at)} · ${RUN_LABEL[r.status] || r.status}`}
                  style={{
                    display: 'inline-block', width: 6, borderRadius: 2,
                    height: r.status === 'failed' ? 22 : r.status === 'completed' ? 12 : 8,
                    background: r.status === 'failed'
                      ? 'var(--color-error)'
                      : r.status === 'completed'
                        ? 'var(--color-success)'
                        : 'var(--color-border-strong, var(--color-border))',
                    opacity: flow.active ? 1 : 0.55,
                  }}
                />
              ))}
            </div>
          )}
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>{runsSummary}</div>
          {usage && (usage.total_cost > 0 || budget) && (
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', fontSize: '0.8125rem', flexWrap: 'wrap', justifyContent: 'flex-end' }}>
              <span title={usage.basis}>
                ≈ <strong>{money(usage.today_cost, currency)}</strong> today · {money(usage.last_7_days_cost, currency)} last 7 days
              </span>
              {budget && (
                <span
                  className={budget.reached ? 'badge badge-error' : 'badge'}
                  title="Daily budget (UTC day), enforced by IOFlow"
                >
                  {budget.reached
                    ? 'Budget reached'
                    : `${Math.min(100, Math.round(((budget.spent_today || 0) / budget.daily) * 100))}% of ${money(budget.daily, budget.currency)}/day`}
                </span>
              )}
            </div>
          )}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <button
              className={flow.active ? 'btn btn-secondary' : 'btn btn-primary'}
              style={{ padding: '2px 10px', fontSize: '0.8125rem' }}
              onClick={onToggleActive}
              disabled={!canEdit || busy}
              title={canEdit ? undefined : 'Only people who can edit this app can pause or activate flows'}
            >
              {busy ? (
                <span className="spinner" style={{ width: 14, height: 14, borderWidth: 2 }} />
              ) : flow.active ? 'Pause' : 'Activate'}
            </button>
            <button
              className="btn btn-secondary"
              style={{ padding: '2px 10px', fontSize: '0.8125rem' }}
              onClick={() => onOpen('flow', flow.flow_id)}
            >
              <span className="material-icons" style={{ fontSize: 15, marginRight: 4 }}>open_in_new</span>
              Open in IOFlow
            </button>
            <button
              className="btn btn-secondary"
              style={{ padding: '2px 6px' }}
              onClick={onToggleOpen}
              aria-expanded={open}
              aria-label={open ? 'Hide runs and cost' : 'Show runs and cost'}
              title={open ? 'Hide runs and cost' : 'Show runs and cost'}
            >
              <span className="material-icons" style={{ fontSize: 17 }}>{open ? 'expand_less' : 'expand_more'}</span>
            </button>
          </div>
        </div>
      </div>

      {open && (
        <UsagePanel
          usage={usage || null}
          usageError={flow.usage_error || ''}
          canEdit={canEdit}
          busy={busy}
          onSaveBudget={onSaveBudget}
        />
      )}

      {open && (
        <div style={{ borderTop: '1px solid var(--color-border)', padding: '0.6rem 0.75rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
            <span style={{ fontSize: '0.8625rem', fontWeight: 600 }}>Recent runs</span>
            <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
              last {runs.length} · from IOFlow’s run history
            </span>
            <span style={{ flex: 1 }} />
            <button
              className="btn btn-secondary"
              style={{ padding: '2px 10px', fontSize: '0.8125rem' }}
              onClick={() => onOpen('executions')}
            >
              All runs in IOFlow
            </button>
          </div>
          {runs.length === 0 ? (
            <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
              {flow.runs_error ? 'Runs could not be read.' : 'This flow has not run yet.'}
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8625rem', minWidth: 520 }}>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
                    {['When', 'Result', 'Took', 'Cost', 'Started by', 'Detail'].map((h) => (
                      <th key={h} style={{ padding: '4px 8px', fontWeight: 500, borderBottom: '1px solid var(--color-border)' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {runs.map((r, i) => (
                    <tr key={r.execution_id || i}>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}>{whenText(r.started_at)}</td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--color-border)' }}>
                        <span
                          className={
                            r.status === 'failed' ? 'badge badge-error' : r.status === 'completed' ? 'badge badge-success' : 'badge'
                          }
                        >
                          {RUN_LABEL[r.status] || r.status || '—'}
                        </span>
                      </td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                        {durationText(r.duration_seconds)}
                      </td>
                      <td
                        style={{ padding: '5px 8px', borderBottom: '1px solid var(--color-border)', whiteSpace: 'nowrap' }}
                        title={(r.cost_meters || []).map((m) => `${quantityText(m.quantity)} ${m.label}: ${money(m.cost, currency)}`).join('\n') || undefined}
                      >
                        {r.cost == null ? <span style={{ color: 'var(--color-text-secondary)' }}>—</span> : money(r.cost, currency)}
                      </td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--color-border)', color: 'var(--color-text-secondary)' }}>
                        {TRIGGER_LABEL[r.trigger_type] || r.trigger_type || '—'}
                      </td>
                      <td style={{ padding: '5px 8px', borderBottom: '1px solid var(--color-border)', color: r.error ? 'var(--color-error)' : 'var(--color-text-secondary)' }}>
                        {r.error || '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}

interface UsagePanelProps {
  usage: FlowUsage | null
  usageError: string
  canEdit: boolean
  busy: boolean
  onSaveBudget: (daily: number | null) => void
}

/** What the flow's paid API calls used and cost, and its daily budget. */
function UsagePanel({ usage, usageError, canEdit, busy, onSaveBudget }: UsagePanelProps) {
  const budget = usage?.budget || null
  const [draft, setDraft] = useState<string>(budget ? String(budget.daily) : '')
  useEffect(() => {
    setDraft(budget ? String(budget.daily) : '')
  }, [budget])
  const currency = usage?.currency || 'USD'
  const total = usage?.total_cost || 0
  const draftValue = draft.trim() === '' ? null : Number(draft)
  const draftValid = draftValue === null || (Number.isFinite(draftValue) && draftValue > 0)
  const changed = (draftValue ?? null) !== (budget?.daily ?? null)
  const cell = { padding: '5px 8px', borderBottom: '1px solid var(--color-border)' } as const

  return (
    <div style={{ borderTop: '1px solid var(--color-border)', padding: '0.6rem 0.75rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6, flexWrap: 'wrap' }}>
        <span style={{ fontSize: '0.8625rem', fontWeight: 600 }}>Usage &amp; cost</span>
        <span style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
          paid API calls · last {usage?.days || 30} days · estimated by IOFlow
        </span>
      </div>

      {!usage ? (
        <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-warning)' }}>
          {usageError ? `IOFlow couldn’t report this flow’s usage: ${usageError}` : 'No usage reported.'}
        </p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '0.5rem', marginBottom: '0.6rem' }}>
            <SummaryBox label="Today (UTC)" value={money(usage.today_cost, currency)} />
            <SummaryBox label="Last 7 days" value={money(usage.last_7_days_cost, currency)} />
            <SummaryBox label={`Last ${usage.days} days`} value={money(usage.total_cost, currency)} />
          </div>

          {budget?.reached && (
            <div
              role="alert"
              style={{
                display: 'flex', gap: 6, alignItems: 'flex-start', fontSize: '0.8625rem', marginBottom: '0.6rem',
                color: 'var(--color-error)', background: 'var(--color-error-weak)', borderRadius: 6, padding: '4px 8px',
              }}
            >
              <span className="material-icons" style={{ fontSize: 16, marginTop: 1 }}>money_off</span>
              <span>
                Daily budget reached ({money(budget.spent_today, budget.currency)} of {money(budget.daily, budget.currency)}).
                New runs stop until midnight UTC — raise the budget to continue today.
              </span>
            </div>
          )}

          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: '0.6rem', fontSize: '0.8625rem' }}>
            <label htmlFor="flow-budget" style={{ fontWeight: 600 }}>Daily budget</label>
            {canEdit ? (
              <>
                <input
                  id="flow-budget"
                  type="number"
                  min={0}
                  step="0.5"
                  inputMode="decimal"
                  placeholder="No limit"
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  style={{ width: 110 }}
                  aria-invalid={!draftValid}
                />
                <span style={{ color: 'var(--color-text-secondary)' }}>{currency} per UTC day</span>
                <button
                  className="btn btn-primary"
                  style={{ padding: '2px 10px', fontSize: '0.8125rem' }}
                  disabled={busy || !draftValid || !changed}
                  onClick={() => onSaveBudget(draftValue)}
                >
                  Save
                </button>
                {budget && (
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '2px 10px', fontSize: '0.8125rem' }}
                    disabled={busy}
                    onClick={() => onSaveBudget(null)}
                  >
                    Remove
                  </button>
                )}
              </>
            ) : (
              <span>{budget ? `${money(budget.daily, budget.currency)} per UTC day` : 'No limit'}</span>
            )}
            <span style={{ flexBasis: '100%', color: 'var(--color-text-secondary)', fontSize: '0.8125rem' }}>
              When a day’s cost reaches it, IOFlow stops new runs and further paid calls until midnight UTC.
              {!draftValid && <span style={{ color: 'var(--color-error)' }}> Enter an amount above 0, or leave it empty for no limit.</span>}
            </span>
          </div>

          {usage.by_step.length === 0 ? (
            <p style={{ margin: 0, fontSize: '0.8625rem', color: 'var(--color-text-secondary)' }}>
              No paid API calls in this period.
            </p>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.8625rem', minWidth: 520 }}>
                <caption style={{ textAlign: 'left', fontWeight: 600, padding: '2px 0 4px' }}>Where the cost comes from</caption>
                <thead>
                  <tr style={{ textAlign: 'left', color: 'var(--color-text-secondary)' }}>
                    {['Step', 'Calls', 'Used', 'Cost', 'Share'].map((h) => (
                      <th key={h} style={{ ...cell, fontWeight: 500 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {usage.by_step.map((b) => (
                    <tr key={`${b.node_id}:${b.meter}:${b.dimension}`}>
                      <td style={cell}>
                        <div>{b.label}</div>
                        <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                          {[b.app_name, b.service_name].filter(Boolean).join(' · ')}
                        </div>
                      </td>
                      <td style={{ ...cell, color: 'var(--color-text-secondary)' }}>{b.calls_metered.toLocaleString()}</td>
                      <td style={cell}>
                        {quantityText(b.quantity)} {b.meter_label.toLowerCase()}
                        {b.dimension && (
                          <span style={{ marginLeft: 6, fontFamily: 'monospace', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
                            {b.dimension}
                          </span>
                        )}
                      </td>
                      <td style={{ ...cell, whiteSpace: 'nowrap' }}>
                        {b.cost == null ? <span title="No price known for this — counted, not costed">unpriced</span> : money(b.cost, currency)}
                      </td>
                      <td style={{ ...cell, color: 'var(--color-text-secondary)' }}>
                        {b.cost != null && total > 0 ? `${Math.round((b.cost / total) * 100)}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ marginTop: '0.5rem', fontSize: '0.8125rem', color: 'var(--color-text-secondary)', display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span>{usage.basis}</span>
            {usage.has_unpriced && <span style={{ color: 'var(--color-warning)' }}>Some calls have no known price: they are counted but not costed.</span>}
            {usage.price_notes.map((n) => (
              <span key={n.note}>
                {n.app_name ? <strong style={{ fontWeight: 600 }}>{n.app_name}: </strong> : null}
                {n.note}
              </span>
            ))}
          </div>
        </>
      )}
    </div>
  )
}
